import { trackEvent } from '@patrianna/analytics'
import {
  closeDialogByName,
  getDialogStack,
  getDialogVisibilityByName,
  openDialog,
  removeShownSessionDialog,
  getShownSessionDialogs,
} from '@patrianna/dialog-manager'
import { uniqueItemsInArray } from '@patrianna/shared-utils/helpers'

import getAccountBonusesRequest from 'services/gateway/requests/getAccountBonuses'
import { isSevenDaysRewardsEnabledSelector } from 'store/modules/brandConfig/selectors'

import { sweepstakeEnabledSelector } from '../appConfig/selectors'
import { openPendingDialogIfExists } from '../ui/actions'

import {
  getDailyBonusesSelector,
  getDelayedBonusesSelector,
  getInvitationBonusesSelector,
  getManualAndInvitationBonusesSelector,
  getManualBonusesSelector,
  getOrderedManualAndInvitationBonusesSelector,
  wheelsSelector,
} from './selectors'
import { actions } from './slice'
import { getTodayBonus, onWheelSpinEnd } from './utils'

import type { InvitationBonusInfo } from '@patrianna/shared-patrianna-types/store/AccountBonusesModule'
import type { BaseBonus, InvitationBonus } from '@patrianna/shared-patrianna-types/store/BonusesFlowModule'
import type {
  AcceptDailyBonusRequest,
  AcceptInvitationBonusRequest,
  AcceptManualBonusRequest,
} from '@patrianna/shared-patrianna-types/websocket/requests'
import type {
  AcceptDailyBonusResponse,
  AcceptInvitationBonusResponse,
  AcceptManualBonusResponse,
} from '@patrianna/shared-patrianna-types/websocket/response'
import type { ReduxState, TypedThunk } from 'src/store'

export const {
  saveDailyBonuses,
  saveDelayedBonuses,
  saveManualBonuses,
  saveInvitatonBonuses,
  receivedBonuses,
  setBonusesLoading,
  saveWheels,
  setWheelSpinning,
  setBonusesLoaded,
  setIsRewardDialogShown,
  setWheelFreeSpinsClaimState,
  setAcceptingDailyBonus,
} = actions

export const acceptBonusFlow =
  <T extends BaseBonus>(
    code: string,
    selector: (state: ReduxState) => T[],
    saveFunction: (data: T[]) => any,
    updateDelayedBonuses: (codeI: string) => any,
  ): TypedThunk =>
  (dispatch, getState) => {
    const bonuses = selector(getState())
    const filteredBonuses = bonuses.filter((el) => el.code !== code)
    saveFunction(filteredBonuses)
    dispatch(updateDelayedBonuses(code))
  }

export const updateBonusesNotification =
  <T extends BaseBonus>(
    bonus: T,
    selector: (state: ReduxState) => T[],
    saveFunction: (data: T[]) => any,
    checkUserBonus: (bonus: BaseBonus) => any,
    isInvitationBonus = false,
  ): TypedThunk =>
  (dispatch, getState) => {
    const bonuses = selector(getState())
    const updatedBonuses = bonuses?.length ? [...bonuses.map((el) => ({ ...el })), bonus] : [bonus]
    const rewardDialogWasShown = getShownSessionDialogs()?.includes('REWARD_DIALOG')
    const isDialogOpen = !!getDialogStack()?.length

    if (rewardDialogWasShown && !isDialogOpen) {
      // if dialog was shown and no visible dialogs - we show reward to user
      saveFunction(updatedBonuses)
      dispatch(checkUserBonus(bonus))
    } else if (rewardDialogWasShown && isDialogOpen) {
      // if dialog was shown and another dialog is visible - we clean shown history and save only assigned bonus - since previous bonuses were shown in current session
      // it will open one by one with isRewardAvailable flag
      if (isInvitationBonus) {
        saveFunction(updatedBonuses)
        removeShownSessionDialog('REWARD_DIALOG')
      } else {
        saveFunction([bonus])
        removeShownSessionDialog('REWARD_DIALOG')
      }
    } else {
      // if dialog wasn't open before - we skip opening and let DialogProvider open it with isRewardAvailable flag
      saveFunction(updatedBonuses)
    }
  }

export const checkNextUserBonus = (code: string, callBack: (bonus: BaseBonus) => void) => (bonuses: BaseBonus[]) => {
  const currentGameIndex = bonuses.findIndex((el) => el.code === code)
  const lastGameIndex = bonuses.length - 1

  if (currentGameIndex < lastGameIndex) {
    const nextBonus = bonuses[currentGameIndex + 1]

    callBack(nextBonus)
  }
}

export const tryOpenRewardDialog =
  (bonus: BaseBonus, isFromNotification = false): TypedThunk =>
  () => {
    const isBonusDialogOpened = getDialogVisibilityByName('REWARD_DIALOG')

    if (!isBonusDialogOpened) {
      openDialog({ dialogName: 'REWARD_DIALOG', dialogProps: { bonus, isFromNotification } })
    }
  }

export const checkUserBonuses = (): TypedThunk => (dispatch, getState) => {
  const state = getState()
  const orderedBonuses = getOrderedManualAndInvitationBonusesSelector(state)
  const firstBonus = orderedBonuses[0]

  if (firstBonus) {
    dispatch(tryOpenRewardDialog(firstBonus))
  }
}

export const checkUserBonusFromNotification =
  (bonus: BaseBonus): TypedThunk =>
  (dispatch, getState) => {
    if (!bonus) {
      return
    }

    const isScAvailable = sweepstakeEnabledSelector(getState())
    const adjustedBonus = {
      ...bonus,
      sweepstakeAmount: isScAvailable ? bonus.sweepstakeAmount : null,
    }

    if (adjustedBonus.sweepstakeAmount || adjustedBonus.goldAmount) {
      dispatch(tryOpenRewardDialog(adjustedBonus, true))
    }
  }

export const openNextBonusDialog =
  (code: string): TypedThunk =>
  (_dispatch, getState) => {
    checkNextUserBonus(code, (bonus: BaseBonus) => openDialog({ dialogName: 'REWARD_DIALOG', dialogProps: { bonus } }))(
      getOrderedManualAndInvitationBonusesSelector(getState()),
    )
  }

// delayed bonuses flow
export const addDelayedBonus =
  (delayedBonus: BaseBonus): TypedThunk =>
  (dispatch, getState) => {
    const delayedBonuses = getDelayedBonusesSelector(getState())
    const updatedBonuses = delayedBonuses
      ? uniqueItemsInArray([...delayedBonuses, delayedBonus], 'code')
      : [delayedBonus]
    dispatch(saveDelayedBonuses({ delayedBonuses: updatedBonuses }))
  }

export const addAllBonusesToDelayArray = (): TypedThunk => (dispatch, getState) => {
  const allBonuses = getManualAndInvitationBonusesSelector(getState())
  dispatch(saveDelayedBonuses({ delayedBonuses: allBonuses }))
}

const removeDelayedBonusByCode =
  (code: string): TypedThunk =>
  (dispatch, getState) => {
    const bonuses = getDelayedBonusesSelector(getState())
    const filteredBonuses = bonuses?.filter((el) => el.code !== code)
    dispatch(saveDelayedBonuses({ delayedBonuses: filteredBonuses }))
    closeDialogByName({ dialogName: 'REWARD_DIALOG' })
  }

// acceptt and  ubdate bonus functions
export const acceptManualBonusResponse =
  (code: string): TypedThunk =>
  (dispatch) => {
    const saveBonuses = (bonuses: Array<BaseBonus>) => {
      dispatch(
        saveManualBonuses({
          manualBonuses: bonuses,
        }),
      )
    }
    dispatch(acceptBonusFlow(code, getManualBonusesSelector, saveBonuses, removeDelayedBonusByCode))
  }

export const acceptInvitationBonusResponse =
  (code: string): TypedThunk =>
  (dispatch) => {
    const saveBonuses = (bonuses: Array<InvitationBonus | InvitationBonusInfo>) => {
      dispatch(
        saveInvitatonBonuses({
          invitationBonuses: bonuses,
        }),
      )
    }
    dispatch(acceptBonusFlow(code, getInvitationBonusesSelector, saveBonuses, removeDelayedBonusByCode))
  }

export const updateManualBonusesNotificationListener =
  (bonus: BaseBonus): TypedThunk =>
  (dispatch) => {
    const saveBonuses = (bonuses: Array<BaseBonus>) => {
      dispatch(
        saveManualBonuses({
          manualBonuses: bonuses,
        }),
      )
    }
    dispatch(updateBonusesNotification(bonus, getManualBonusesSelector, saveBonuses, checkUserBonusFromNotification))
  }

export const updateInvitationBonusesNotificationListener =
  (bonus: InvitationBonus | InvitationBonusInfo): TypedThunk =>
  (dispatch) => {
    const saveBonuses = (bonuses: Array<InvitationBonusInfo>) => {
      dispatch(
        saveInvitatonBonuses({
          invitationBonuses: bonuses,
        }),
      )
    }
    dispatch(
      updateBonusesNotification(bonus, getInvitationBonusesSelector, saveBonuses, checkUserBonusFromNotification, true),
    )
  }

export const acceptDailyBonus =
  (source?: string): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const bonuses = getDailyBonusesSelector(getState())
    const todayBonus = getTodayBonus(bonuses)
    const scEnabled = sweepstakeEnabledSelector(getState())
    const sevenDaysEnabled = isSevenDaysRewardsEnabledSelector(getState())
    const version = sevenDaysEnabled
      ? {
          version: '4w1w',
        }
      : {}

    if (todayBonus) {
      const data: AcceptDailyBonusRequest & { version?: string } = {
        type: 'AcceptDailyBonusRequest',
        code: todayBonus.code,
        ...version,
      }

      dispatch(setAcceptingDailyBonus({ accepting: true }))

      gateway
        .emit<AcceptDailyBonusResponse>(data)
        .then(() => {
          dispatch(getAccountBonusesRequest())
          dispatch(setAcceptingDailyBonus({ accepting: false }))
        })
        .catch((err) => {
          console.log('err', err)
          const isOtpRequired = err?.status?.errorCode === 'err_promo_sms_consent_otp_required'
          dispatch(setAcceptingDailyBonus({ accepting: false }))

          if (isOtpRequired) {
            openDialog({
              dialogName: 'SMS_CONSENT_DIALOG',
              dialogProps: {
                source: source,
              },
            })
            trackEvent('sms_popup_appeared')

            return
          }

          dispatch(errorHandler(err, data))
        })
        .finally(() => {
          closeDialogByName({ dialogName: 'DAILY_REWARD_DIALOG' })
          setTimeout(() => {
            dispatch(openPendingDialogIfExists())
          }, 100)
        })

      if (scEnabled) {
        trackEvent('sc_daily_bonus_accepted', {
          category: 'daily SC reward accepted',
          label: todayBonus.code,
        })
      } else {
        trackEvent(
          'gc_daily_bonus_accepted',
          {
            category: 'daily GC reward accepted',
            label: todayBonus.code,
          },
          {
            bloomreach: false,
          },
        )
      }
    } else {
      closeDialogByName({ dialogName: 'DAILY_REWARD_DIALOG' })
    }
  }

export const acceptUserBonusByTypeRequest =
  (code: string, onError: () => void): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const manualBonuses = getManualBonusesSelector(getState())
    const invitationBonuses = getInvitationBonusesSelector(getState())
    if (manualBonuses.find((el) => el.code === code)) {
      const data: AcceptManualBonusRequest = { type: 'AcceptManualBonusRequest', code }

      gateway
        .emit<AcceptManualBonusResponse>(data)
        .then((body) => {
          dispatch(acceptManualBonusResponse(body.code))
        })
        .catch((err) => {
          dispatch(errorHandler(err, data))
          onError()
        })
    } else if (invitationBonuses.find((el) => el.code === code)) {
      const data: AcceptInvitationBonusRequest = { type: 'AcceptInvitationBonusRequest', code }

      gateway
        .emit<AcceptInvitationBonusResponse>(data)
        .then((body) => {
          dispatch(acceptInvitationBonusResponse(body.code))
        })
        .catch((err) => {
          dispatch(errorHandler(err, data))
          onError()
        })
    }
  }

export const decreaseWheelSpins =
  (code: string): TypedThunk =>
  (dispatch, getState) => {
    const wheels = wheelsSelector(getState())

    const updatedWheels = wheels.map((wheel) => {
      if (wheel.code === code) {
        return { ...wheel, spins: wheel.spins - 1 }
      }

      return wheel
    })

    dispatch(saveWheels(updatedWheels))
  }

export const setWheelNextSpinAt =
  (code: string, nextSpinAt: string): TypedThunk =>
  async (dispatch, getState) => {
    await onWheelSpinEnd(getState)

    const wheels = wheelsSelector(getState())

    const updatedWheels = wheels.map((wheel) => {
      if (wheel.code === code) {
        return { ...wheel, nextSpinAt }
      }

      return wheel
    })

    dispatch(saveWheels(updatedWheels))
  }
